import Button from '@mui/material/Button';
import withStyles from '@mui/styles/withStyles';
import InProgressIcon from 'components/in-progress-icon';
import {ButtonContext} from 'hooks/button-context';
import isNil from 'lodash/isNil';
import React, {useContext, useMemo} from 'react';

import type {ButtonProps} from '@mui/material/Button/Button';
import type {Theme} from '@mui/material/styles';
import type {Styles} from '@mui/styles';

export type TextButtonProps = Pick<ButtonProps, 'fullWidth' | 'id' | 'onClick' | 'size' | 'startIcon' | 'type'> & {
    critical?: boolean;
    disabled: boolean;
    inProgress: boolean;
    label: string;
    samaritan?: boolean;
    variant?: 'primary' | 'secondary' | 'tertiary';
    ['data-heap-id']?: string;
};

const TextButton = (props: TextButtonProps) => {
    const {
        critical = false,
        disabled: disabledProp,
        fullWidth = false,
        id,
        inProgress,
        label,
        onClick,
        samaritan,
        size,
        startIcon,
        type,
        variant: variantProp = 'primary',
    } = props;

    const context = useContext(ButtonContext);

    const {size: contextSize, disabled: contextDisabled} = context;
    // Explicit props takes precedence. Context will be used if no prop provided.
    // Default value will be used if neither are provided.
    let buttonSize: ButtonProps['size'];
    if (!isNil(size)) {
        buttonSize = size;
    } else if (!isNil(contextSize)) {
        buttonSize = contextSize;
    } else {
        buttonSize = 'medium';
    }
    // Disabled uses union of values, so if anything is providing a true value
    // The button will be disabled
    let disabled = disabledProp || !!contextDisabled;
    const color = critical ? 'secondary' : 'primary';

    let variant: ButtonProps['variant'];
    if (variantProp === 'primary') {
        variant = 'contained';
    } else if (variantProp === 'secondary') {
        variant = 'outlined';
    } else {
        variant = 'text';
    }

    let inProgressIcon = startIcon;
    if (inProgress) {
        inProgressIcon = <InProgressIcon />;
        disabled = true;
    }

    const ButtonComponent = useMemo(() => {
        if (samaritan) {
            return SamaritanTextButton;
        } else {
            return StandardTextButton;
        }
    }, [samaritan]);

    return (
        <ButtonComponent
            color={color}
            disabled={disabled}
            disableElevation
            disableRipple
            fullWidth={fullWidth}
            id={id}
            onClick={onClick}
            // This is a coalesced value from react context and props
            size={buttonSize}
            startIcon={inProgressIcon}
            type={type}
            variant={variant}
            data-heap-id={props['data-heap-id']}>
            {label}
        </ButtonComponent>
    );
};

export default React.memo(TextButton);

const getBaseStyles = (theme: Theme): Styles<Theme, ButtonProps> => ({
    root: {
        height: '4rem',
        minWidth: '6.4rem',
        fontSize: theme.typography.componentM.fontSize,
        lineHeight: theme.typography.componentM.lineHeight,
        fontWeight: theme.typography.fontWeightBold as number,
        whiteSpace: 'nowrap',
        borderRadius: '0.6rem',
        // Default transition with 'color' added to match 'background-color'
        transition:
            'color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
        '&.Mui-focusVisible': {
            outlineColor: theme.palette.common.black,
            outlineStyle: 'solid',
            outlineWidth: '0.4rem',
            outlineOffset: '0.2rem',
            zIndex: 3,
        },
    },
    sizeSmall: {
        height: '3.2rem',
        fontSize: theme.typography.componentS.fontSize,
        lineHeight: theme.typography.componentS.lineHeight,
        borderRadius: '0.4rem',
    },
    sizeLarge: {
        height: '5.6rem',
        fontSize: theme.typography.componentM.fontSize,
        lineHeight: theme.typography.componentM.lineHeight,
        fontWeight: theme.typography.fontWeightBold as number,
        borderRadius: '0.8rem',
    },
    textSizeSmall: theme.typography.componentXs,
});

const StandardTextButton = withStyles((theme) => ({
    ...getBaseStyles(theme),
    outlinedPrimary: {
        color: theme.palette.primary['500'],
        borderColor: theme.palette.primary['500'],
        backgroundColor: theme.palette.common.white,
        '&:active': {
            color: theme.palette.primary['700'],
            borderColor: theme.palette.primary['700'],
            backgroundColor: theme.palette.common.white,
        },
        '&:focus:not(:active)': {
            color: theme.palette.primary['400'],
            borderColor: theme.palette.primary['400'],
            backgroundColor: theme.palette.common.white,
        },
        '&:hover': {
            color: theme.palette.primary['400'],
            borderColor: theme.palette.primary['400'],
            backgroundColor: theme.palette.common.white,
        },
    },
    outlinedSecondary: {
        color: theme.palette.error['500'],
        borderColor: theme.palette.error['500'],
        backgroundColor: theme.palette.common.white,
        '&:active': {
            color: theme.palette.error['700'],
            borderColor: theme.palette.error['700'],
            backgroundColor: theme.palette.common.white,
        },
        '&:hover': {
            color: theme.palette.error['400'],
            borderColor: theme.palette.error['400'],
            backgroundColor: theme.palette.common.white,
        },
        '&:focus:not(:active)': {
            color: theme.palette.error['400'],
            borderColor: theme.palette.error['400'],
            backgroundColor: theme.palette.common.white,
        },
    },
    containedSecondary: {
        color: theme.palette.common.white,
        backgroundColor: theme.palette.error['500'],
        '&:active': {
            backgroundColor: theme.palette.error['800'],
        },
        '&:disabled': {
            color: theme.palette.grey['400'],
            backgroundColor: theme.palette.grey['100'],
        },
        '&:focus:not(:active)': {
            backgroundColor: theme.palette.error['700'],
        },
        '&:hover': {
            backgroundColor: theme.palette.error['700'],
        },
    },
    containedPrimary: {
        color: theme.palette.common.white,
        backgroundColor: theme.palette.primary['500'],
        '&:active': {
            backgroundColor: theme.palette.primary['700'],
        },
        '&:disabled': {
            color: theme.palette.grey['400'],
            backgroundColor: theme.palette.grey['100'],
        },
        '&:focus:not(:active)': {
            backgroundColor: theme.palette.primary['400'],
        },
        '&:hover': {
            backgroundColor: theme.palette.primary['400'],
        },
    },
    text: {
        border: 'none',
        color: theme.palette.primary['500'],
        '&:hover': {
            background: 'none',
            textDecoration: 'underline',
        },
    },
    textPrimary: {
        color: theme.palette.primary['500'],
        '&:active': {
            backgroundColor: theme.palette.primary['100'],
            color: theme.palette.primary['700'],
        },
        '&:focus:not(:active)': {
            backgroundColor: theme.palette.primary['20'],
        },
        '&:hover': {
            backgroundColor: theme.palette.primary['20'],
            textDecoration: 'none',
        },
    },
    textSecondary: {
        color: theme.palette.error['500'],
        '&:active': {
            backgroundColor: theme.palette.error['100'],
            color: theme.palette.error['700'],
        },
        '&:focus:not(:active)': {
            backgroundColor: theme.palette.error['20'],
        },
        '&:hover': {
            backgroundColor: theme.palette.error['20'],
            textDecoration: 'none',
        },
    },
}))(Button);

const SamaritanTextButton = withStyles((theme) => ({
    ...getBaseStyles(theme),
    containedPrimary: {
        color: theme.palette.common.white,
        background: `linear-gradient(90deg, ${theme.palette.samaritan['500']} 0%, ${theme.palette.indigo['500']} 100%)`,
        '&:hover': {
            background: `linear-gradient(90deg, ${theme.palette.samaritan['700']} 0%, ${theme.palette.indigo['700']} 100%)`,
        },
        '&:active': {
            background: `linear-gradient(90deg, ${theme.palette.samaritan['800']} 0%, ${theme.palette.indigo['800']} 100%)`,
        },
        '&:disabled': {
            color: theme.palette.grey['400'],
            background: theme.palette.grey['100'],
        },
        '&:focus:not(:active)': {
            background: `linear-gradient(90deg, ${theme.palette.samaritan['700']} 0%, ${theme.palette.indigo['700']} 100%)`,
        },
    },
    outlinedPrimary: {
        color: theme.palette.lilac['500'],
        borderColor: theme.palette.lilac['500'],
        '&:hover': {
            color: theme.palette.lilac['700'],
            borderColor: theme.palette.lilac['700'],
        },
        '&:active': {
            color: theme.palette.lilac['800'],
            borderColor: theme.palette.lilac['800'],
        },
        '&:focus:not(:active)': {
            color: theme.palette.lilac['700'],
            borderColor: theme.palette.lilac['700'],
        },
    },
    text: {
        border: 'none',
        color: theme.palette.samaritan['600'],
        '&:hover': {
            background: 'none',
            textDecoration: 'underline',
        },
    },
    textPrimary: {
        color: theme.palette.samaritan['600'],
        '&:hover': {
            background: `linear-gradient(90deg, ${theme.palette.samaritan['20']} 0%, ${theme.palette.indigo['20']} 100%)`,
            color: theme.palette.samaritan['700'],
            textDecoration: 'none',
        },
        '&:active': {
            background: `linear-gradient(90deg, ${theme.palette.samaritan['50']} 0%, ${theme.palette.indigo['50']} 100%)`,
            color: theme.palette.samaritan['800'],
        },
        '&:focus:not(:active)': {
            background: `linear-gradient(90deg, ${theme.palette.samaritan['20']} 0%, ${theme.palette.indigo['20']} 100%)`,
            color: theme.palette.samaritan['700'],
        },
    },
}))(Button);
